import { Info, Typography, useMobile } from '@sonnen/web-ui';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

type Position = 'top' | 'right' | 'bottom' | 'left';

type TooltipProps = {
  content: ReactNode;
  id?: string;
  position?: Position;
  className?: string;
  dataTestId?: string;
  ariaLabel?: string;
};

const Tooltip = ({
  content,
  id,
  position = 'top',
  className = '',
  dataTestId = 'tooltip',
  ariaLabel,
}: TooltipProps) => {
  const [visible, setVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipId = id || `tooltip-${Math.random().toString(36).substr(2, 9)}`;
  const isMobile = useMobile();

  const updatePosition = () => {
    if (!triggerRef.current || !tooltipRef.current) return;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    const { scrollLeft = 0, scrollTop = 0 } = document.documentElement;

    let top = 0;
    let left = 0;

    switch (position) {
      case 'top':
        top = triggerRect.top + scrollTop - tooltipRect.height - 8;
        left = triggerRect.left + scrollLeft + triggerRect.width / 2 - 240;
        break;
      case 'right':
        top = triggerRect.top + scrollTop + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.right + scrollLeft + 8;
        break;
      case 'bottom':
        top = triggerRect.bottom + scrollTop;
        left = triggerRect.left + scrollLeft - tooltipRect.width + triggerRect.width;
        break;
      case 'left':
        top = triggerRect.top + scrollTop + triggerRect.height / 2 - tooltipRect.height / 2;
        left = triggerRect.left + scrollLeft - tooltipRect.width - 8;
        break;
    }
    setTooltipPosition({ top, left });
  };

  const showTooltip = () => {
    setVisible(true);
    updatePosition();
  };

  const hideTooltip = () => setVisible(false);

  const toggleTooltip = () => (visible ? hideTooltip() : showTooltip());

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      toggleTooltip();
    }
  };

  const onEscapeKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && visible) hideTooltip();
  };

  const onClickOutside = (e: MouseEvent) => {
    const targetNode = e.target as Node;
    const container = containerRef.current;
    const trigger = triggerRef.current;
    const tooltip = tooltipRef.current;

    if (isMobile) {
      if (container && !container.contains(targetNode)) hideTooltip();
    } else {
      if (trigger && !trigger.contains(targetNode) && tooltip && !tooltip.contains(targetNode))
        hideTooltip();
    }
  };

  useEffect(() => {
    if (!visible) return;

    document.addEventListener('keydown', onEscapeKey);
    document.addEventListener('click', onClickOutside);

    if (!isMobile) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
    }

    return () => {
      document.removeEventListener('keydown', onEscapeKey);
      document.removeEventListener('click', onClickOutside);

      if (!isMobile) {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      }
    };
  }, [visible, isMobile, position]);

  const tooltipContentClasses = `
    ${styles.tooltipContent}
    ${styles[`tooltip${position.charAt(0).toUpperCase() + position.slice(1)}`]}
    ${className}
  `;

  const renderTooltipContent = () => {
    const tooltipContent = (
      <div
        id={tooltipId}
        ref={tooltipRef}
        role="tooltip"
        className={tooltipContentClasses}
        style={
          !isMobile
            ? { top: `${tooltipPosition.top}px`, left: `${tooltipPosition.left}px` }
            : undefined
        }
        data-testid={dataTestId}
      >
        <Typography.Body1>{content}</Typography.Body1>
      </div>
    );

    return isMobile ? tooltipContent : createPortal(tooltipContent, document.body);
  };

  return (
    <div ref={containerRef} className={styles.tooltipContainer}>
      <div
        ref={triggerRef}
        className={styles.tooltipTrigger}
        onMouseEnter={!isMobile ? showTooltip : undefined}
        onMouseLeave={!isMobile ? hideTooltip : undefined}
        onFocus={!isMobile ? showTooltip : undefined}
        onBlur={!isMobile ? hideTooltip : undefined}
        onClick={isMobile ? toggleTooltip : undefined}
        onKeyDown={onKeyDown}
        aria-describedby={visible ? tooltipId : undefined}
        data-testid={`${dataTestId}-trigger`}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel || undefined}
      >
        <Info color="primary" size="lg" />
      </div>

      {visible && renderTooltipContent()}
    </div>
  );
};

export default Tooltip;
